<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Prodotti acquistati</h1>
          <p class="subtitle">Elenco completo dei prodotti acquistati da ordini WooCommerce, legacy e manuali.</p>
        </div>

        <section class="card-surface filters-card">
          <div class="filters-row">
            <div class="filters-group">
              <label for="pp-email">Email</label>
              <input id="pp-email" v-model.trim="filters.email" type="text" placeholder="email utente" />
            </div>
            <div class="filters-group">
              <label for="pp-product">Prodotto</label>
              <input id="pp-product" v-model.trim="filters.product" type="text" placeholder="nome prodotto" />
            </div>
            <div class="filters-group">
              <label for="pp-source">Sorgente</label>
              <select id="pp-source" v-model="filters.source">
                <option value="">Tutte</option>
                <option value="woocommerce">WooCommerce</option>
                <option value="legacy">Legacy</option>
                <option value="manuale">Manuale</option>
              </select>
            </div>
            <div class="filters-group">
              <label for="pp-from">Dal</label>
              <input id="pp-from" v-model="filters.fromDate" type="date" />
            </div>
            <div class="filters-group">
              <label for="pp-to">Al</label>
              <input id="pp-to" v-model="filters.toDate" type="date" />
            </div>
            <div class="filters-actions">
              <button type="button" class="btn ghost" @click="resetFilters" :disabled="loading">Reset</button>
              <button type="button" class="btn primary staff-button-neutral" @click="loadOrders" :disabled="loading">
                {{ loading ? 'Caricamento…' : 'Carica ordini' }}
              </button>
            </div>
          </div>
          <p v-if="lastUpdatedAt" class="filters-note">
            Ultimo aggiornamento: {{ formatDateTime(lastUpdatedAt) }} · Ordini letti: {{ ordersCount }}
            <span v-if="loadedByEmail" class="muted">(filtro email)</span>
          </p>
        </section>

        <section class="card-surface" v-if="error">
          <p class="error-state">{{ error }}</p>
        </section>

        <section class="card-surface" v-else-if="loading">
          <p class="muted">Caricamento ordini…</p>
        </section>

        <section class="card-surface" v-else>
          <div class="summary-row">
            <div><strong>Prodotti unici:</strong> {{ aggregatedProducts.length }}</div>
            <div><strong>Ordini:</strong> {{ summary.orders }}</div>
            <div><strong>Quantità totale:</strong> {{ summary.quantity }}</div>
          </div>

          <table v-if="aggregatedProducts.length" class="lines-table">
            <thead>
              <tr>
                <th>Prodotto</th>
                <th>SKU / ID</th>
                <th>Ordini</th>
                <th>Quantità</th>
                <th>Ultimo acquisto</th>
                <th>Primo acquisto</th>
                <th>Sorgenti</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in aggregatedProducts" :key="product.key">
                <td class="line-product">{{ product.productName }}</td>
                <td class="mono">
                  <span v-if="product.sku">{{ product.sku }}</span>
                  <span v-else-if="product.productId">{{ product.productId }}</span>
                  <span v-else>—</span>
                </td>
                <td>{{ product.orderCount }}</td>
                <td>{{ product.quantity }}</td>
                <td>{{ product.lastOrderLabel }}</td>
                <td>{{ product.firstOrderLabel }}</td>
                <td class="source-list">
                  <span
                    v-for="source in product.sources"
                    :key="`${product.key}-${source}`"
                    class="badge-src"
                    :data-src="source"
                  >
                    {{ source }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <p v-else class="muted">Nessun prodotto trovato con i filtri correnti.</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { getFirestore, collection, getDocs, query, orderBy, startAfter, limit, where } from 'firebase/firestore';
import { resolveOrderTimeMs } from '@/lib/topSku';
import SidebarComponent from '../SidebarComponent.vue';

const ORDER_PAGE_SIZE = 400;

export default {
  name: 'ProdottiAcquistati',
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const loading = ref(false);
    const error = ref('');
    const ordersCount = ref(0);
    const lastUpdatedAt = ref(null);
    const loadedByEmail = ref(false);
    const rawLines = ref([]);

    const filters = reactive({
      email: '',
      product: '',
      source: '',
      fromDate: '',
      toDate: '',
    });

    const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

    const looksLikeExactEmail = (value) => {
      const trimmed = (value || '').trim();
      if (!trimmed) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    };

    const normalizeSource = (order, docId) => {
      const explicit = (order?.source || order?._source || '').toString().trim().toLowerCase();
      if (explicit) return explicit;
      const id = (docId || order?.orderId || '').toString().toLowerCase();
      if (id.startsWith('legacy_')) return 'legacy';
      if (id.startsWith('mysql_')) return 'legacy';
      if (id.startsWith('manual_')) return 'manuale';
      if (id.startsWith('export_')) return 'woocommerce';
      if (order?.createdBy) return 'manuale';
      return 'woocommerce';
    };

    const safeProducts = (order) => {
      const candidates = [order?.products, order?.line_items, order?.lineItems, order?.items];
      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length) return candidate;
      }
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
      return [];
    };

    const resolveLineName = (line = {}) => {
      const candidates = [
        line.name,
        line.title,
        line.productName,
        line.product_name,
        line.product_title,
        line.item_name,
        line.itemName,
      ];
      for (const candidate of candidates) {
        if (candidate) return String(candidate);
      }
      return '';
    };

    const resolveLineQuantity = (line = {}) => {
      const qtyFields = ['quantity', 'qty', 'amount_qty', 'line_qty'];
      for (const field of qtyFields) {
        const raw = Number(line[field]);
        if (Number.isFinite(raw) && raw !== 0) return Math.abs(raw);
      }
      return 1;
    };

    const resolveLinePrice = (line = {}) => {
      const amountFields = ['total', 'line_total', 'subtotal', 'amount', 'line_subtotal', 'row_total'];
      for (const field of amountFields) {
        const raw = Number(line[field]);
        if (Number.isFinite(raw) && raw !== 0) return raw;
      }
      const priceFields = ['price', 'unit_price', 'line_price', 'unitPrice'];
      for (const field of priceFields) {
        const raw = Number(line[field]);
        if (Number.isFinite(raw) && raw !== 0) return raw;
      }
      return null;
    };

    const resolveLineSku = (line = {}) => {
      const candidates = [line.sku, line.SKU, line.productSku];
      for (const candidate of candidates) {
        if (candidate) return String(candidate);
      }
      return '';
    };

    const resolveLineProductId = (line = {}) => {
      const candidates = [line.productId, line.product_id, line.id, line.itemId, line.catalogId];
      for (const candidate of candidates) {
        if (candidate !== undefined && candidate !== null && candidate !== '') return String(candidate);
      }
      return '';
    };

    const resolveOrderEmail = (order = {}) => {
      const candidates = [
        order.userEmail,
        order.customerEmail,
        order.email,
        order.customer_email,
        order.billing?.email,
      ];
      for (const candidate of candidates) {
        if (candidate) return String(candidate).trim();
      }
      return '';
    };

    const resolveOrderStatus = (order = {}) => {
      const candidates = [order.status, order.statusOrder, order.orderStatus, order.paymentStatus];
      for (const candidate of candidates) {
        if (candidate) return String(candidate);
      }
      return '';
    };

    const formatDateTime = (value) => {
      if (!value) return '—';
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString('it-IT');
    };

    const formatDateFromMs = (ms) => {
      if (!ms) return '—';
      const date = new Date(ms);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString('it-IT');
    };

    const buildLinesFromOrder = (docId, order, collector) => {
      const source = normalizeSource(order, docId);
      const orderId = order?.orderId || docId || '—';
      const orderDateMs = resolveOrderTimeMs(order) || 0;
      const orderDateLabel = formatDateFromMs(orderDateMs);
      const email = resolveOrderEmail(order);
      const status = resolveOrderStatus(order);
      let products = safeProducts(order);

      if (!products.length) {
        const fallbackName = order?.productName || order?.product_title || order?.productTitle || order?.name || order?.title;
        if (fallbackName) {
          products = [{
            name: fallbackName,
            quantity: order?.quantity || order?.qty || 1,
            price: order?.price || order?.totalPrice || 0,
          }];
        }
      }

      products.forEach((line, idx) => {
        const productName = resolveLineName(line) || 'Senza nome';
        const quantity = resolveLineQuantity(line);
        const price = resolveLinePrice(line);
        const sku = resolveLineSku(line);
        const productId = resolveLineProductId(line);
        const key = `${docId}_${idx}_${sku || productId || productName}`;
        collector.push({
          key,
          orderDocId: docId,
          orderId,
          orderDateMs,
          orderDateLabel,
          source,
          email,
          status,
          productName,
          quantity,
          price,
          sku,
          productId,
        });
      });
    };

    const loadOrdersByEmail = async (email) => {
      const trimmed = (email || '').trim();
      const normalized = normalizeText(trimmed);
      const ordersRef = collection(db, 'orders');
      const docsById = new Map();

      const attempts = [];
      const pushAttempt = (field, value) => {
        if (!field || !value) return;
        attempts.push({ field, value });
      };

      pushAttempt('userEmail', trimmed);
      pushAttempt('customerEmail', trimmed);
      if (normalized && normalized !== trimmed) {
        pushAttempt('userEmail', normalized);
        pushAttempt('customerEmail', normalized);
      }

      for (const attempt of attempts) {
        try {
          const snap = await getDocs(query(ordersRef, where(attempt.field, '==', attempt.value)));
          snap.forEach((docSnap) => {
            if (!docsById.has(docSnap.id)) {
              docsById.set(docSnap.id, docSnap.data());
            }
          });
        } catch (err) {
          console.warn(`Prodotti acquistati: query fallita (${attempt.field} == ${attempt.value})`, err);
        }
      }

      const collector = [];
      docsById.forEach((order, docId) => {
        buildLinesFromOrder(docId, order, collector);
      });

      return { lines: collector, orders: docsById.size };
    };

    const loadAllOrders = async () => {
      const ordersRef = collection(db, 'orders');
      let lastDoc = null;
      let total = 0;
      const collector = [];

      let hasMore = true;
      while (hasMore) {
        const q = lastDoc
          ? query(ordersRef, orderBy('__name__'), startAfter(lastDoc), limit(ORDER_PAGE_SIZE))
          : query(ordersRef, orderBy('__name__'), limit(ORDER_PAGE_SIZE));
        const snap = await getDocs(q);
        if (snap.empty) {
          hasMore = false;
          break;
        }

        snap.forEach((docSnap) => {
          buildLinesFromOrder(docSnap.id, docSnap.data(), collector);
        });

        total += snap.size;
        lastDoc = snap.docs[snap.docs.length - 1];
        if (snap.size < ORDER_PAGE_SIZE) {
          hasMore = false;
        }
      }

      return { lines: collector, orders: total };
    };

    const loadOrders = async () => {
      if (loading.value) return;
      loading.value = true;
      error.value = '';
      ordersCount.value = 0;
      rawLines.value = [];
      loadedByEmail.value = false;

      try {
        const email = (filters.email || '').trim();
        let result;
        if (looksLikeExactEmail(email)) {
          result = await loadOrdersByEmail(email);
          loadedByEmail.value = true;
        } else {
          result = await loadAllOrders();
        }
        rawLines.value = result.lines;
        ordersCount.value = result.orders;
        lastUpdatedAt.value = new Date();
      } catch (err) {
        console.error('Errore caricamento prodotti acquistati:', err);
        error.value = 'Errore durante il caricamento degli ordini.';
      } finally {
        loading.value = false;
      }
    };

    const resetFilters = () => {
      filters.email = '';
      filters.product = '';
      filters.source = '';
      filters.fromDate = '';
      filters.toDate = '';
    };

    const filteredLines = computed(() => {
      const emailNeedle = normalizeText(filters.email);
      const productNeedle = normalizeText(filters.product);
      const sourceNeedle = normalizeText(filters.source);
      const fromMs = filters.fromDate ? new Date(`${filters.fromDate}T00:00:00`).getTime() : 0;
      const toMs = filters.toDate ? new Date(`${filters.toDate}T23:59:59.999`).getTime() : 0;

      return rawLines.value
        .filter((line) => {
          if (sourceNeedle && normalizeText(line.source) !== sourceNeedle) return false;
          if (emailNeedle && !normalizeText(line.email).includes(emailNeedle)) return false;
          if (productNeedle && !normalizeText(line.productName).includes(productNeedle)) return false;
          if ((fromMs || toMs) && !line.orderDateMs) return false;
          if (fromMs && line.orderDateMs < fromMs) return false;
          if (toMs && line.orderDateMs > toMs) return false;
          return true;
        })
        .slice()
        .sort((a, b) => (b.orderDateMs || 0) - (a.orderDateMs || 0));
    });

    const aggregatedProducts = computed(() => {
      const map = new Map();
      const buildKey = (line) => {
        const nameKey = normalizeText(line.productName);
        return nameKey ? `name:${nameKey}` : `name:senza_nome`;
      };

      filteredLines.value.forEach((line) => {
        const key = buildKey(line);
        let entry = map.get(key);
        if (!entry) {
          entry = {
            key,
            productName: line.productName || 'Senza nome',
            skuSet: new Set(),
            productIdSet: new Set(),
            quantity: 0,
            orderCount: 0,
            ordersSet: new Set(),
            sourcesSet: new Set(),
            firstOrderMs: 0,
            lastOrderMs: 0,
          };
          map.set(key, entry);
        }

        entry.quantity += Number(line.quantity) || 0;
        if (line.sku) entry.skuSet.add(line.sku);
        if (line.productId) entry.productIdSet.add(line.productId);
        if (line.orderDocId) entry.ordersSet.add(line.orderDocId);
        if (line.source) entry.sourcesSet.add(line.source);
        if (line.orderDateMs) {
          if (!entry.firstOrderMs || line.orderDateMs < entry.firstOrderMs) {
            entry.firstOrderMs = line.orderDateMs;
          }
          if (!entry.lastOrderMs || line.orderDateMs > entry.lastOrderMs) {
            entry.lastOrderMs = line.orderDateMs;
          }
        }
      });

      return Array.from(map.values())
        .map((entry) => ({
          key: entry.key,
          productName: entry.productName,
          sku: entry.skuSet.size === 1 ? Array.from(entry.skuSet)[0] : (entry.skuSet.size > 1 ? 'varie' : ''),
          productId: entry.productIdSet.size === 1 ? Array.from(entry.productIdSet)[0] : (entry.productIdSet.size > 1 ? 'varie' : ''),
          quantity: entry.quantity,
          orderCount: entry.ordersSet.size,
          sources: Array.from(entry.sourcesSet).sort(),
          firstOrderLabel: formatDateFromMs(entry.firstOrderMs),
          lastOrderLabel: formatDateFromMs(entry.lastOrderMs),
          lastOrderMs: entry.lastOrderMs,
        }))
        .sort((a, b) => (b.lastOrderMs || 0) - (a.lastOrderMs || 0));
    });

    const summary = computed(() => {
      const orders = new Set();
      let quantity = 0;
      filteredLines.value.forEach((line) => {
        if (line.orderDocId) orders.add(line.orderDocId);
        quantity += Number(line.quantity) || 0;
      });
      return {
        orders: orders.size,
        quantity,
      };
    });

    onMounted(loadOrders);

    return {
      filters,
      loading,
      error,
      ordersCount,
      lastUpdatedAt,
      loadedByEmail,
      filteredLines,
      aggregatedProducts,
      summary,
      loadOrders,
      resetFilters,
      formatDateTime,
    };
  },
};
</script>

<style scoped>
.card-surface {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  padding: 20px 24px;
  margin-top: 18px;
}

.filters-card {
  margin-top: 24px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: flex-end;
}

.filters-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filters-group label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.filters-group input,
.filters-group select {
  min-width: 180px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
}

.filters-group input:focus,
.filters-group select:focus {
  outline: none;
  border-color: #2563eb;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.filters-actions {
  margin-left: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.filters-note {
  margin-top: 12px;
  color: #64748b;
  font-size: 0.85rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #ffffff;
}

.btn.ghost {
  background: #f1f5f9;
  color: #0f172a;
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 16px;
  font-size: 0.95rem;
  color: #0f172a;
}

.lines-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}

.lines-table th {
  text-align: left;
  padding: 10px 12px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.lines-table td {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}

.line-product {
  font-weight: 600;
  color: #0f172a;
}

.source-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mono {
  font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.badge-src {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #e2e8f0;
  color: #475569;
}

.badge-src[data-src="woocommerce"] {
  background: #e0efff;
  color: #1d4ed8;
}

.badge-src[data-src="legacy"] {
  background: #fee2e2;
  color: #b91c1c;
}

.badge-src[data-src="manuale"] {
  background: #ecfdf3;
  color: #15803d;
}

.muted {
  color: #64748b;
}

.error-state {
  color: #b91c1c;
  font-weight: 600;
}

.subtitle {
  color: #64748b;
  margin-top: 6px;
}
</style>
